import Utils from './Utils';

/** Same id as Tickettype.ONE_TIME_PASS — keep local to avoid models circular imports in tests. */
const ONE_TIME_PASS_ID = 'one-time-pass';

export const PDF_LAYOUT_TARGETS = ['paper', 'thermal', 'card'];

export const PDF_LAYOUT_KINDS = [
    'one_time_pass',
    'ticket',
    'cart_receipt',
    'closing',
    'ticket_bookings',
];

export const PDF_KIND_TO_LAYOUT_TYPE = {
    ticket: 'ticket',
    one_time_pass: 'ticket',
    cart_receipt: 'cart-receipt',
    closing: 'closing',
    ticket_bookings: 'ticket-bookings',
};

export const EMAIL_LAYOUT_KEYS = [
    'order_confirmation',
    'gift_notification',
    'refund_confirmation',
    'booking_summary',
    'distributor_report',
];

export const EMAIL_LAYOUT_KEY_TYPES = {
    order_confirmation:   ['order'],
    gift_notification:    ['gift'],
    refund_confirmation:  ['refund'],
    booking_summary:      ['booking'],
    distributor_report:   ['distributor-report'],
};

export function pdfLayoutKindLabel(kind, { tickettypes = [], lang, defaultLang, t }) {
    if (kind === 'one_time_pass') {
        const oneTimePass = tickettypes.find(tt => tt._id === ONE_TIME_PASS_ID);
        if (oneTimePass?.name)
            return Utils.localized_or_fallback(oneTimePass.name, lang, defaultLang);
    }

    return t(`settings.pdf_layouts.${kind}`);
}

export function resolveSettingsPdfLayoutId(setting, kind, target) {
    return setting?.pdf_layouts?.[kind]?.[target] || null;
}

export function resolveTickettypePdfLayoutId({
    setting,
    tickettypeId,
    overrideLayouts,
    target,
}) {
    const overrideId = overrideLayouts?.[target];
    if (overrideId)
        return overrideId;

    const kind = tickettypeId === ONE_TIME_PASS_ID ? 'one_time_pass' : 'ticket';
    return resolveSettingsPdfLayoutId(setting, kind, target);
}

export function pdfLayoutResolutionSource(setting, kind, target, configuredId) {
    if (configuredId)
        return 'global';

    if (setting?.pdf_layouts?.[kind]?.[target])
        return 'global';

    return 'legacy';
}

export function showsPdfTicketLegacyWarning(kind, target) {
    return (kind === 'one_time_pass' || kind === 'ticket')
        && (target === 'paper' || target === 'thermal');
}

export function resolvePdfLayoutDocument(layoutId, storedLayouts = []) {
    if (!layoutId)
        return null;

    return (storedLayouts || []).find(layout => layout._id === layoutId) || null;
}

/**
 * Editor URL for a PDF layout, optionally seeding the preview tickettype/pricing.
 */
export function pdf_layout_edit_path(layoutId, { tickettypeId, pricingKey } = {}) {
    if (!layoutId)
        return null;

    const path = `/layouts/edit/${encodeURIComponent(layoutId)}`;
    const params = new URLSearchParams();
    if (tickettypeId)
        params.set('tickettype', tickettypeId);
    if (pricingKey)
        params.set('pricing', pricingKey);
    const query = params.toString();
    return query ? `${path}?${query}` : path;
}

/**
 * OTP preview selects use `pricinglistId:pricingKey`. Resolve that compound value
 * when only a pricing key is known (tickettype PDF override cards).
 */
export function find_otp_pricing_select_value(pricinglists, pricingKey) {
    if (!pricingKey)
        return null;

    for (const list of pricinglists || []) {
        if (list?.pricings?.[pricingKey])
            return `${list._id}:${pricingKey}`;
    }
    return null;
}

export function resolveEmailLayoutDocument(layoutId, storedEmailLayouts = []) {
    if (!layoutId)
        return null;

    return (storedEmailLayouts || []).find(layout => layout._id === layoutId) || null;
}

export function resolveLayoutDisplayName(layoutId, { pdfLayouts = [], emailLayouts = [] }, lang, defaultLang) {
    if (!layoutId)
        return '';

    const pdfLayout = resolvePdfLayoutDocument(layoutId, pdfLayouts);
    if (pdfLayout?.name)
        return Utils.localized_or_fallback(pdfLayout.name, lang, defaultLang) || layoutId;

    const emailLayout = resolveEmailLayoutDocument(layoutId, emailLayouts);
    if (emailLayout?.name)
        return Utils.localized_or_fallback(emailLayout.name, lang, defaultLang) || layoutId;

    return layoutId;
}
